/**
 * Subscription schemas
 */
import { z } from "zod";
import { createEntityFilterSchema, CurrencyCodeSchema, DynamicFeeBracketSchema, NestedPaginationParamsSchema, SortParamsSchema, StrapiEntitySchema, StrapiIdSchema, } from "./common.schemas";
import { LazyBotConfigRelation, LazyChargeRelation, LazyPaymentMethodRelation, LazyPaymentRelation, LazyStrategyRelation, LazyUserRelation, SubscriptionRelationSchema, SubscriptionTierRelationSchema, } from "./relations";
// ============================================================================
// Enums (SubscriptionStatusSchema is in common.schemas.ts)
// ============================================================================
/**
 * Pricing model schema (component)
 */
export const PricingModelSchema = z.object({
    id: StrapiIdSchema.nullish(),
    name: z.string().nullish(),
    code: z.string().nullish(),
    monthlyPrice: z.number().nullish(),
    currency: z.string().nullish(),
    dynamicFee: z.record(z.unknown()).nullish(),
    vpsOverride: z.number().nullish(),
});
// DynamicFeeBracketSchema is imported from common.schemas.ts to avoid circular dependencies
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Subscription filters schema - typesafe filters for subscription queries
 * Created using createEntityFilterSchema to reuse field types from SubscriptionRelationSchema
 */
export const SubscriptionFiltersSchema = createEntityFilterSchema(SubscriptionRelationSchema, {
    scalars: ["pm_paymentProcessor", "pm_status", "pm_currentPeriodStart", "pm_currentPeriodEnd"],
    relations: ["strategy", "pm_user"],
});
/**
 * Subscription query params schema
 */
export const SubscriptionQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: SubscriptionFiltersSchema.optional(),
})
    .strict();
/**
 * Subscription tier features schema
 */
export const SubscriptionTierFeaturesSchema = z.record(z.string(), z.union([z.boolean(), z.string(), z.number()]));
/**
 * Subscription tier input schema
 */
export const SubscriptionTierInputSchema = z
    .object({
    name: z.string().min(1, "Tier name is required"),
    code: z.string().optional(),
    description: z.string().optional(),
    monthlyPrice: z.number().nonnegative("Monthly price must be non-negative").optional(),
    currency: CurrencyCodeSchema.optional(),
    features: SubscriptionTierFeaturesSchema.optional(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    vpsOverride: z.number().optional(),
    vpsText: z.string().optional(),
    tierText: z.string().optional(),
    profitFeeText: z.string().optional(),
    discountText: z.string().optional(),
    dynamicFee: z.array(DynamicFeeBracketSchema).optional(),
    commissionOnlyBilling: z.boolean().optional(),
})
    .strict();
/**
 * Subscription tier query params schema
 */
export const SubscriptionTierQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z
        .object({
        isActive: z.object({ $eq: z.boolean().optional() }).optional(),
        isDefault: z.object({ $eq: z.boolean().optional() }).optional(),
        code: z.object({ $eq: z.string().optional() }).optional(),
    })
        .optional(),
})
    .strict();
/**
 * User migration input schema
 */
export const UserMigrationInputSchema = z
    .object({
    userIds: z.array(StrapiIdSchema).optional(),
    fromTierId: StrapiIdSchema.optional(),
    toTierId: StrapiIdSchema,
})
    .strict();
/**
 * Free period package input schema - for admin creation
 * Matches adminCreatePackage controller in Strapi
 */
export const FreePeriodPackageInputSchema = z
    .object({
    exchange_account: StrapiIdSchema,
    startDate: z.string(),
    endDate: z.string(),
    reason: z.string().optional(),
    grantType: z.enum(["admin", "registration", "promotion", "referral", "compensation", "migration"]).optional(),
})
    .strict();
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Subscription schema - extends SubscriptionRelationSchema with additional fields and relations
 */
export const SubscriptionSchema = SubscriptionRelationSchema.extend({
    // Additional pm_* fields not in relation schema
    pm_processor_subscriptionId: z.string().nullish(),
    // Pricing model component
    pricingModel: PricingModelSchema.nullish(),
    // Relations (populated) - using lazy schemas to break circular deps
    strategy: LazyStrategyRelation.nullish(),
    bot_configs: z.array(LazyBotConfigRelation).nullish(),
    pm_user: LazyUserRelation.nullish(),
    pm_charges: z.array(LazyChargeRelation).nullish(),
    pm_payments: z.array(LazyPaymentRelation).nullish(),
    pm_paymentMethod: LazyPaymentMethodRelation.nullish(),
});
/**
 * Subscription tier schema - extends SubscriptionTierRelationSchema
 * Override features to use the typed schema instead of record
 */
export const SubscriptionTierSchema = SubscriptionTierRelationSchema.extend({
    // Override with typed features schema
    features: SubscriptionTierFeaturesSchema.nullish(),
    // Added by the controller's find() method (user count per tier)
    userCount: z.number().optional(),
});
/**
 * Response schema for subscription-tiers list endpoint.
 * The controller returns tiers with userCount and meta.usersWithoutTier.
 */
export const SubscriptionTierListResponseSchema = z.object({
    data: z.array(SubscriptionTierSchema),
    meta: z.object({
        usersWithoutTier: z.number().optional(),
    }),
});
/**
 * Free period package schema - response from getPackagesByCredentials
 * Matches the shape returned by findByCredentialsId controller
 */
export const FreePeriodPackageSchema = z.object({
    id: StrapiIdSchema,
    startDate: z.string().nullish(),
    endDate: z.string().nullish(),
    isActive: z.boolean().nullish(),
    createdAt: z.string().nullish(),
    grantType: z.enum(["admin", "registration", "promotion", "referral", "compensation", "migration"]).nullish(),
    reason: z.string().nullish(),
    targetUser: z
        .object({
        id: StrapiIdSchema.nullish(),
        name: z.string().nullish(),
        email: z.string().nullish(),
    })
        .nullish(),
    grantedBy: z
        .object({
        id: StrapiIdSchema.nullish(),
        name: z.string().nullish(),
        email: z.string().nullish(),
    })
        .nullish(),
    exchange_account: z
        .object({
        id: StrapiIdSchema.nullish(),
        uuid: z.string().nullish(),
        name: z.string().nullish(),
        status: z.string().nullish(),
    })
        .nullish(),
});
/**
 * Grant schema - for subscription grant history
 * Used when admin extends free period for a subscription
 */
export const GrantSchema = StrapiEntitySchema.extend({
    daysAdded: z.number().nullish(),
    reason: z.string().nullish(),
    grantedBy: z
        .object({
        id: StrapiIdSchema.nullish(),
        username: z.string().nullish(),
        name: z.string().nullish(),
    })
        .nullish(),
});
/**
 * Free period package creation response
 * Matches adminCreatePackage controller response in Strapi
 */
export const FreePeriodPackageCreateResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    package: StrapiEntitySchema.extend({
        startDate: z.string().nullish(),
        endDate: z.string().nullish(),
        isActive: z.boolean().nullish(),
        grantType: z.string().nullish(),
        reason: z.string().nullish(),
    }),
});
